import { useState, useEffect } from 'react';
import { FaFlag, FaEdit, FaTrash, FaCheck } from 'react-icons/fa';
import { fetchGoals, createGoal, updateGoal, deleteGoal } from './api';

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({
    name: '',
    description: '',
    targetValue: 0,
    matchType: 'equals',
    condition: {
      type: 'eventCondition',
      parameterValues: {
        eventType: '',
        properties: []
      }
    }
  });

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async () => {
    const data = await fetchGoals();
    setGoals(data || []);
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    await createGoal(newGoal);
    setShowCreateForm(false);
    setNewGoal({
      name: '',
      description: '',
      targetValue: 0,
      matchType: 'equals',
      condition: {
        type: 'eventCondition',
        parameterValues: {
          eventType: '',
          properties: []
        }
      }
    });
    loadGoals();
  };

  const handleUpdateGoal = async (goalId, updatedData) => {
    await updateGoal(goalId, updatedData);
    setEditingGoal(null);
    loadGoals();
  };

  const renderGoalForm = (goal = newGoal, isEditing = false) => (
    <div className="goal-form">
      <h2>{isEditing ? 'Edit Goal' : 'Create New Goal'}</h2>
      <form onSubmit={isEditing ? 
        (e) => {
          e.preventDefault();
          handleUpdateGoal(goal.itemId, goal);
        } : 
        handleCreateGoal
      }>
        <div className="form-group">
          <label>Goal Name</label>
          <input
            type="text"
            value={goal.name}
            onChange={(e) => isEditing ? 
              setEditingGoal({...goal, name: e.target.value}) :
              setNewGoal({...newGoal, name: e.target.value})
            }
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={goal.description}
            onChange={(e) => isEditing ?
              setEditingGoal({...goal, description: e.target.value}) :
              setNewGoal({...newGoal, description: e.target.value})
            }
          />
        </div>

        <div className="form-group">
          <label>Target Value</label>
          <input
            type="number"
            value={goal.targetValue}
            onChange={(e) => isEditing ?
              setEditingGoal({...goal, targetValue: parseInt(e.target.value)}) :
              setNewGoal({...newGoal, targetValue: parseInt(e.target.value)})
            }
            required
          />
        </div>

        <div className="form-group">
          <label>Match Type</label>
          <select
            value={goal.matchType}
            onChange={(e) => isEditing ?
              setEditingGoal({...goal, matchType: e.target.value}) :
              setNewGoal({...newGoal, matchType: e.target.value})
            }
          >
            <option value="equals">Equals</option>
            <option value="greaterThan">Greater Than</option>
            <option value="lessThan">Less Than</option>
          </select>
        </div>

        <div className="form-group">
          <label>Event Type</label>
          <input
            type="text"
            value={goal.condition.parameterValues.eventType}
            onChange={(e) => {
              const newValue = e.target.value;
              if (isEditing) {
                setEditingGoal({
                  ...goal,
                  condition: {
                    ...goal.condition,
                    parameterValues: {
                      ...goal.condition.parameterValues,
                      eventType: newValue
                    }
                  }
                });
              } else {
                setNewGoal({
                  ...newGoal,
                  condition: {
                    ...newGoal.condition,
                    parameterValues: {
                      ...newGoal.condition.parameterValues,
                      eventType: newValue
                    }
                  }
                });
              }
            }}
            required
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            {isEditing ? 'Update Goal' : 'Create Goal'}
          </button>
          <button 
            type="button" 
            className="cancel-btn"
            onClick={() => isEditing ? setEditingGoal(null) : setShowCreateForm(false)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="goals-container">
      <div className="goals-header">
        <h1>Goals Management</h1>
        <button 
          className="create-btn"
          onClick={() => setShowCreateForm(true)}
        >
          Create Goal
        </button>
      </div>

      {showCreateForm && renderGoalForm()}
      {editingGoal && renderGoalForm(editingGoal, true)}

      <div className="goals-grid">
        {goals.map(goal => (
          <div key={goal.itemId} className="goal-card">
            <div className="goal-header">
              <FaFlag className="goal-icon" />
              <h3>{goal.name}</h3>
            </div>
            <p className="goal-description">{goal.description}</p>
            <div className="goal-details">
              <div className="detail-item">
                <span className="label">Target Value</span>
                <span className="value">{goal.targetValue}</span>
              </div>
              <div className="detail-item">
                <span className="label">Match Type</span>
                <span className="value">{goal.matchType}</span>
              </div>
              <div className="detail-item">
                <span className="label">Event Type</span>
                <span className="value">{goal.condition.parameterValues.eventType}</span>
              </div>
            </div>
            <div className="goal-actions">
              <button 
                className="action-btn edit"
                onClick={() => setEditingGoal(goal)}
              >
                <FaEdit />
              </button>
              <button 
                className="action-btn delete"
                onClick={() => deleteGoal(goal.itemId)}
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Goals; 